import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SaleFormModal from '../components/sales/SaleFormModal';
import SaleDetailModal from '../components/sales/SaleDetailModal';
import { salesApi, resolveSalesApiError } from '../services/salesApi';
import { productSetupApi, resolveProductSetupApiError } from '../services/productSetupApi';
import { agentApi, resolveAgentApiError } from '../services/agentApi';
import { branchApi, resolveBranchApiError } from '../services/branchApi';
import useScopePermissions from '../hooks/useScopePermissions';
import {
  matchesDateRange,
  todayIso,
  triggerCsvDownload,
  triggerExcelLikeDownload,
} from '../utils/exportUtils';
import TablePagination, { usePagination } from '../components/TablePagination';
import './SalesPage.css';

function formatCurrency(value) {
  if (value === null || value === undefined) {
    return '-';
  }

  return `PHP ${Number(value).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function SalesPage() {
  const { hasScope } = useScopePermissions();
  const canView = hasScope('sales.view');
  const canCreate = hasScope('sales.create');
  const canUpdate = hasScope('sales.update');
  const canDelete = hasScope('sales.delete');

  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReferences, setIsLoadingReferences] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [viewingSaleId, setViewingSaleId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    date_from: '',
    date_to: '',
  });

  const viewingSale = useMemo(
    () => sales.find((sale) => Number(sale.id) === Number(viewingSaleId)) || null,
    [sales, viewingSaleId]
  );

  const filteredSales = useMemo(
    () => sales.filter((sale) => {
      const search = filters.search.toLowerCase();
      const searchableText = [
        sale.transaction_no,
        sale.company_client_name,
        sale.contact_person,
        sale.product_name,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');

      const matchesSearch = searchableText.includes(search);
      const matchesStatus = filters.status === 'all' || sale.status === filters.status;
      const matchesDates = matchesDateRange(sale.date, filters.date_from, filters.date_to);
      return matchesSearch && matchesStatus && matchesDates;
    }),
    [sales, filters]
  );

  const pagination = usePagination(filteredSales);

  const loadSales = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      const response = await salesApi.listSales({ bustCache: true });
      const list = response?.data?.sales || response?.sales || [];
      setSales(Array.isArray(list) ? list : []);
      if (clearFeedback && list.length === 0) {
        setMessage('No sales found.');
      }
    } catch (err) {
      setSales([]);
      setError(resolveSalesApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadReferences = useCallback(async () => {
    setIsLoadingReferences(true);

    try {
      const [productsResponse, agentsResponse, branchesResponse] = await Promise.all([
        productSetupApi.listProductCommissions({ bustCache: true }),
        agentApi.listAgents({ bustCache: true }),
        branchApi.listBranches({ bustCache: true }),
      ]);

      const productList = productsResponse?.data?.product_commissions || productsResponse?.product_commissions || [];
      const agentList = agentsResponse?.data?.agents || agentsResponse?.agents || [];
      const branchList = branchesResponse?.data?.branches || branchesResponse?.branches || [];

      setProducts(Array.isArray(productList) ? productList : []);
      setAgents(Array.isArray(agentList) ? agentList : []);
      setBranches(Array.isArray(branchList) ? branchList : []);
    } catch (err) {
      const productError = resolveProductSetupApiError(err);
      const agentError = resolveAgentApiError(err);
      const branchError = resolveBranchApiError(err);
      setError(productError || agentError || branchError || 'Failed to load reference data.');
    } finally {
      setIsLoadingReferences(false);
    }
  }, []);

  useEffect(() => {
    loadSales();
    loadReferences();
  }, [loadSales, loadReferences]);

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((previous) => ({ ...previous, [name]: value }));
  }

  const EXPORT_HEADERS = ['Date', 'Transaction No.', 'Client / Company', 'Contact Person', 'Contact Number', 'Product', 'Plan', 'Premium', 'Qty', 'Total', 'Payment', 'Terms', 'Status'];

  function buildExportRows() {
    return filteredSales.map((sale) => [
      sale.date || '',
      sale.transaction_no || '',
      sale.company_client_name || '',
      sale.contact_person || '',
      sale.contact_number || '',
      sale.product_name || '',
      sale.plan_name || sale.plan_items?.[0]?.plan_name || '',
      sale.premium_amount ?? '',
      sale.qty_availed ?? sale.plan_items?.[0]?.qty ?? '',
      sale.total_amount ?? '',
      sale.mode_of_payment || '',
      sale.payment_terms || '',
      sale.status || '',
    ]);
  }

  function exportCsv() {
    triggerCsvDownload(`sales-${todayIso()}.csv`, EXPORT_HEADERS, buildExportRows());
  }

  function exportExcel() {
    triggerExcelLikeDownload(`sales-${todayIso()}.xls`, EXPORT_HEADERS, buildExportRows());
  }

  function openAddForm() {
    if (!canCreate) {
      setError('You do not have permission to add sales.');
      return;
    }

    setIsEditMode(false);
    setSelectedSale(null);
    setError('');
    setIsFormOpen(true);
  }

  function openEditForm(sale) {
    if (!canUpdate) {
      setError('You do not have permission to edit sales.');
      return;
    }

    setIsEditMode(true);
    setSelectedSale(sale);
    setError('');
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
    setSelectedSale(null);
  }

  async function handleSubmit(payload) {
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      if (isEditMode && !canUpdate) {
        setError('You do not have permission to edit sales.');
        return;
      }

      if (!isEditMode && !canCreate) {
        setError('You do not have permission to add sales.');
        return;
      }

      if (isEditMode && selectedSale) {
        await salesApi.updateSale(selectedSale.id, payload);
        setMessage('Sale updated successfully.');
      } else {
        await salesApi.createSale(payload);
        setMessage('Sale created successfully.');
      }

      closeForm();
      await loadSales({ clearFeedback: false });
    } catch (err) {
      setError(resolveSalesApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(sale) {
    if (!canDelete) {
      setError('You do not have permission to delete sales.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete sale "${sale.transaction_no || sale.id}"?`)) {
      return;
    }

    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await salesApi.deleteSale(sale.id);
      setMessage('Sale deleted successfully.');
      await loadSales({ clearFeedback: false });
    } catch (err) {
      setError(resolveSalesApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Sales</h1>
        <p>Record and track actual product sales.</p>
      </section>

      <section className="dashboard-content">
        <div className="dashboard-toolbar">
          <div className="dashboard-filters">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search transaction, client, contact, or product..."
            />
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
              title="Date from"
            />
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
              title="Date to"
            />
          </div>
          <div className="dashboard-actions">
            <button type="button" className="profile-secondary-btn" onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="profile-secondary-btn" onClick={exportExcel}>
              Export Excel
            </button>
            {canCreate ? (
              <button type="button" className="profile-secondary-btn" onClick={openAddForm} disabled={isLoadingReferences}>
                + Add Sale
              </button>
            ) : null}
          </div>
        </div>

        {error && !isFormOpen && <div className="error-box">{error}</div>}
        {message && <div className="success-box">{message}</div>}

        {isLoading ? (
          <div className="dashboard-loading">Loading sales...</div>
        ) : filteredSales.length === 0 ? (
          <div className="dashboard-empty">No sales found.</div>
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table sales-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Transaction No.</th>
                  <th>Client / Company</th>
                  <th>Contact Person</th>
                  <th>Product</th>
                  <th>Plan</th>
                  <th>Premium</th>
                  <th>Qty</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Terms</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.pagedItems.map((sale) => {
                  const planName = sale.plan_name || sale.plan_items?.[0]?.plan_name || '-';
                  const qtyAvailed = sale.qty_availed ?? sale.plan_items?.[0]?.qty ?? '-';

                  return (
                    <tr key={sale.id}>
                      <td>{sale.date || '-'}</td>
                      <td className="sales-transaction-cell">{sale.transaction_no || '-'}</td>
                      <td>{sale.company_client_name || '-'}</td>
                      <td>
                        <div>{sale.contact_person || '-'}</div>
                        {sale.contact_number ? (
                          <div className="sales-contact-sub">{sale.contact_number}</div>
                        ) : null}
                      </td>
                      <td>{sale.product_name || '-'}</td>
                      <td>{planName}</td>
                      <td className="sales-number-cell">{formatCurrency(sale.premium_amount)}</td>
                      <td className="sales-number-cell">{qtyAvailed}</td>
                      <td className="sales-number-cell sales-total-cell">{formatCurrency(sale.total_amount)}</td>
                      <td>{sale.mode_of_payment || '-'}</td>
                      <td>{sale.payment_terms || '-'}</td>
                      <td>
                        <span className={`status-badge status-${String(sale.status || '').toLowerCase()}`}>
                          {sale.status || '-'}
                        </span>
                      </td>
                      <td>
                        <div className="dashboard-actions">
                          {canView ? (
                            <button
                              type="button"
                              className="profile-secondary-btn profile-secondary-btn-xs"
                              onClick={() => setViewingSaleId(sale.id)}
                            >
                              View
                            </button>
                          ) : null}
                          {canUpdate ? (
                            <button
                              type="button"
                              className="profile-secondary-btn profile-secondary-btn-xs"
                              onClick={() => openEditForm(sale)}
                            >
                              Edit
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              className="profile-danger-btn profile-danger-btn-xs"
                              onClick={() => handleDelete(sale)}
                              disabled={isSubmitting}
                            >
                              Delete
                            </button>
                          ) : null}
                          {!canView && !canUpdate && !canDelete ? <span>-</span> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading ? <TablePagination {...pagination} /> : null}
      </section>

      <SaleFormModal
        isOpen={isFormOpen}
        onClose={closeForm}
        isEditMode={isEditMode}
        initialSale={selectedSale}
        products={products}
        agents={agents}
        branches={branches}
        isSubmitting={isSubmitting}
        error={error}
        onSubmit={handleSubmit}
      />

      <SaleDetailModal
        isOpen={Boolean(viewingSale)}
        onClose={() => setViewingSaleId(null)}
        sale={viewingSale}
      />
    </div>
  );
}
